import type { Hotspot } from '../api/types.js';
import type { HotspotsEvent, HotspotsSnapshot, HotspotsState } from './types.js';

// Pure reducer. No I/O. Returns same reference for no-op transitions so
// dispatch can short-circuit equality checks.
export function hotspotsTransition(
  state: HotspotsState,
  event: HotspotsEvent,
  hotspotsLen: number,
): HotspotsState {
  // Defensive contract: with zero hotspots the entire UI subsystem is
  // disabled, so every action is a no-op (reference-equal). This also
  // guards CLOSE in the unlikely case openIndex was ever set to a stale
  // index from a prior loaded session before teardown reset state.
  if (hotspotsLen === 0) return state;
  switch (event.type) {
    case 'OPEN': {
      if (event.index < 0 || event.index >= hotspotsLen) return state;
      if (!state.visible) return state; // can't open when hidden
      if (state.openIndex === event.index) return state;
      return { openIndex: event.index, visible: state.visible };
    }
    case 'CLOSE': {
      if (state.openIndex < 0) return state;
      return { openIndex: -1, visible: state.visible };
    }
    case 'NEXT': {
      if (hotspotsLen === 0 || state.openIndex < 0) return state;
      return { openIndex: (state.openIndex + 1) % hotspotsLen, visible: state.visible };
    }
    case 'PREV': {
      if (hotspotsLen === 0 || state.openIndex < 0) return state;
      return {
        openIndex: (state.openIndex - 1 + hotspotsLen) % hotspotsLen,
        visible: state.visible,
      };
    }
    case 'TOGGLE_VISIBILITY': {
      // No-op when there are no hotspots: the toggle would create dead UI
      // affordance with no overlay to show/hide.
      if (hotspotsLen === 0) return state;
      const nextVisible = !state.visible;
      return {
        openIndex: nextVisible ? state.openIndex : -1, // hiding closes popup
        visible: nextVisible,
      };
    }
  }
}

export function hotspotsSnapshot(
  state: HotspotsState,
  hotspots: readonly Hotspot[],
): HotspotsSnapshot {
  const current = state.openIndex >= 0 ? hotspots[state.openIndex] : undefined;
  return {
    openIndex: state.openIndex,
    visible: state.visible,
    total: hotspots.length,
    currentTitle: current?.title ?? '',
    currentDescription: current?.description ?? null,
    currentId: current?.id ?? null,
  };
}

// Stable sort by order_index ASC, then by id (lexical) for tiebreak. Mirrors
// the pattern used by sortPresets (4.F) — backend doesn't guarantee server-
// side ordering, so the client sorts once at scene load.
export function sortHotspots(hotspots: readonly Hotspot[]): readonly Hotspot[] {
  return [...hotspots].sort((a, b) => {
    if (a.order_index !== b.order_index) return a.order_index - b.order_index;
    return a.id.localeCompare(b.id);
  });
}
